import axios from 'axios';
import { ActionTree } from 'vuex';
import {
  mutationAddDocumentTags,
  mutationAddTag,
  mutationDeleteDocuments,
  mutationDeleteFolder,
  mutationEditDocuments,
  mutationError,
  mutationLoadFolderTree,
  mutationLoadingDocuments,
  mutationOpenFolder,
  mutationSetAllTags,
  mutationSetBreadcrumb,
  mutationSetDefaultFilter,
  mutationSetDocumentNotifications,
  mutationSetDocuments,
  mutationSetFilter,
  mutationSetFilterActiveState,
  mutationSetFilterRanges,
  mutationSetFolders,
  mutationSetKeyValues,
  mutationSetSelectedFolder,
  mutationUpdateFolder,
  mutationUpdateSorting,
  mutationAddDocumentNotifications,
  mutationDeleteDocumentNotifications,
  mutationToggleSelectDocument,
  mutationToggleSelectAllDocuments,
  mutationDeselectAllDocuments,
} from '@/contract-management/store/contract-management.mutations';
import {
  IContractManagementFilter,
  IContractManagementState,
  IFilterSetting,
} from '@/contract-management/store/contract-management.store';
import { RootState } from '@/store/store';
import {
  filterIsActive,
  getDefaultFilter,
} from '@/contract-management/helpers/contract-management-filter.helper';
import { translate } from '@/helpers/translation.helper';
import service from '@/api/contract-management/services/contract-management.service';
import { ISorting } from '@/types/Sorting';
import { IContractManagementFilterRanges } from '@/contract-management/types/contract-management-filter-ranges';
import { IContractManagementKeyValues } from '@/contract-management/types/contract-management-key-values';
import { IDocument } from '@/types/Document';
import { IDocumentNotification } from '@/types/DocumentNotification';
import { IDownloadDocument } from '@/types/DownloadDocument';
import { IEditDocumentsPayload } from '@/types/EditDocumentsPayload';
import { IFolder } from '@/folders/types/folder';
import { ITag } from '@/types/Tag';
import { IUploadDocument } from '@/types/UploadDocument';
import { FolderService } from '@/api/folders/services/folder.service';
import { IFolderTree } from '@/folders/types/folder-tree';
import { ICreateNotificationRequest } from '@/types/CreateNotificationRequest';
import { EnumDocumentType } from '@/enums/DocumentType';

export const actionLoadBreadcrumb = 'loadBreadcrumb';
export const actionCreateFolder = 'createFolder';
export const actionLoadFolders = 'loadFolders';
export const actionRemoveDocumentTags = 'removeTags';
export const actionOpenFolder = 'openFolder';
export const actionUpdateDocumentOwner = 'updateDocumentOwner';
export const actionUpdateNotification = 'updateNotification';

export const actionLoadFilterRanges = 'loadFilterRanges';
export const actionApplyFilter = 'applyFilter';
export const actionUpdateFilter = 'updateFilter';
export const actionSetDefaultFilter = 'setDefaultFilter';
export const actionSetFilterActiveStatus = 'setFilterActiveStatus';
export const actionResetFilter = 'resetFilter';
export const actionUpdateFilterDocumentType = 'updateFilterDocumentType';

export const actionSetSelectedFolder = 'setSelectedFolder';
export const actionSetSelectedFolderSuccess = 'setSelectedFolderSuccess';
export const actionSetSelectedFolderFailed = 'setSelectedFolderFailed';

export const actionLoadAllTags = 'loadAllTags';
export const actionLoadAllTagsSuccess = 'loadAllTagsSuccess';

export const actionUpdateDocumentsFolder = 'updateDocumentsFolder';
export const actionUpdateDocumentsFolderSuccess = 'updateDocumentsFolderSuccess';
export const actionUpdateDocumentsFolderFailed = 'updateDocumentsFolderFailed';

export const actionLoadDocuments = 'loadDocuments';
export const actionLoadDocumentsSuccess = 'loadDocumentsSuccess';

export const actionLoadKeyValues = 'loadKeyValues';
export const actionLoadKeyValuesSuccess = 'loadKeyValuesSuccess';

export const actionDeleteDocuments = 'deleteDocuments';
export const actionDeleteDocumentsSuccess = 'deleteDocumentsSuccess';
export const actionDeleteDocumentsFailed = 'deleteDocumentsFailed';

export const actionUploadDocument = 'uploadDocument';
export const actionUploadDocumentSuccess = 'uploadDocumentSuccess';
export const actionUploadDocumentFailed = 'uploadDocumentFailed';

export const actionCreateTag = 'createTags';
export const actionCreateTagSuccess = 'createTagsSuccess';
export const actionCreateTagFailed = 'createTagsFailed';

export const actionAddDocumentTags = 'addTags';
export const actionAddDocumentTagsSuccess = 'addTagsSuccess';
export const actionAddDocumentTagsFailed = 'addTagsFailed';

export const actionRemoveAllTags = 'removeAllTags';
export const actionRemoveAllTagsSuccess = 'removeAllTagsSuccess';

export const actionUpdateFolder = 'updateFolder';
export const actionUpdateFolderSuccess = 'updateFolderSuccess';

export const actionDeleteFolder = 'deleteFolder';
export const actionDeleteFolderSuccess = 'deleteFolderSuccess';
export const actionDeleteFolderFailed = 'deleteFolderFailed';

export const actionDownloadDocuments = 'downloadDocuments';
export const actionDownloadToClient = 'downloadToClient';
export const actionDownloadFailed = 'downloadDocumentsFailed';

export const actionCreateNotifications = 'createNotifications';
export const actionCreateNotificationsSuccess = 'createNotificationsSuccess';
export const actionCreateNotificationsFailed = 'createNotificationsFailed';

export const actionDeleteNotification = 'deleteNotification';
export const actionDeleteNotificationSuccess = 'deleteNotificationSuccess';
export const actionDeleteNotificationFailed = 'deleteNotificationFailed';

export const actionDeleteSingleNotification = 'deleteSingleNotification';

export const actionSortDocuments = 'sortDocuments';
export const actionSortDocumentsSuccess = 'sortDocumentsSuccess';
export const actionSortDocumentsFailed = 'sortDocumentsFailed';

export const actionLoadFolderTree = 'loadFolderTree';
export const actionLoadFolderTreeSuccess = 'loadFolderTreeSuccess';

export const actionUpdateDocuments = 'updateDocuments';
export const actionUpdateDocumentsSuccess = 'updateDocumentsSuccess';
export const actionUpdateDocumentsFailed = 'updateDocumentsFailed';

export const actionLoadDocumentNotifications = 'loadDocumentNotifications';
export const actionLoadDocumentNotificationsSuccess = 'loadDocumentNotificationsSuccess';

export const actionToggleSelectDocument = 'toggleSelectDocument';
export const actionToggleSelectAllDocuments = 'toggleSelectAllDocuments';
export const actionDeselectAllDocuments = 'deselectAllDocuments';

export const actions: ActionTree<IContractManagementState, RootState> = {
  async [actionLoadDocumentNotifications]({ dispatch }) {
    try {
      const documentNotifications: IDocumentNotification[] = await service.loadDocumentNotifications();
      dispatch(actionLoadDocumentNotificationsSuccess, documentNotifications);
    } catch (error) {
      console.error(error);
    }
  },
  async [actionLoadDocumentNotificationsSuccess](
    { commit },
    documentNotifications: IDocumentNotification[]
  ) {
    commit(mutationSetDocumentNotifications, documentNotifications);
  },
  async [actionSortDocuments]({ dispatch, commit }, sorting: ISorting) {
    try {
      commit(mutationUpdateSorting, sorting);
      dispatch(actionApplyFilter);
      dispatch(actionSortDocumentsSuccess);
    } catch (error) {
      dispatch(actionSortDocumentsFailed);
    }
  },
  async [actionSortDocumentsFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('Failed to sort documents'), {
      root: true,
    });
  },
  async [actionToggleSelectDocument]({ commit }, documentId: string) {
    commit(mutationToggleSelectDocument, documentId);
  },
  async [actionToggleSelectAllDocuments]({ commit }) {
    commit(mutationToggleSelectAllDocuments);
  },
  async [actionDeselectAllDocuments]({ commit }) {
    commit(mutationDeselectAllDocuments);
  },
  async [actionLoadFolderTree]({ commit }) {
    try {
      const folderTree: IFolderTree = await FolderService.loadFolderTree();
      folderTree.name = translate(folderTree.name);
      commit(mutationLoadFolderTree, folderTree);
    } catch (error) {
      console.error(error);
    }
  },
  async [actionCreateNotifications]({ dispatch }, notifications: ICreateNotificationRequest[]) {
    try {
      const createdNotifications: IDocumentNotification[] = await service.createNotifications(
        notifications
      );
      dispatch(actionCreateNotificationsSuccess, createdNotifications);
    } catch (error) {
      dispatch(actionCreateNotificationsFailed, { error });
    }
  },
  async [actionCreateNotificationsSuccess](
    { dispatch, commit },
    notifications: IDocumentNotification[]
  ) {
    commit(mutationAddDocumentNotifications, notifications);
    dispatch(
      'alert/showSuccessAlert',
      translate('Reminder successfully added to {documentsCount} documents', {
        documentsCount: notifications.length,
      }),
      {
        root: true,
      }
    );
  },
  async [actionCreateNotificationsFailed]({ dispatch, state }) {
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to add reminder to {documentsCount} documents', {
        documentsCount: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
  },
  async [actionDeleteNotification]({ dispatch, state }) {
    const documentNotificationIds: string[] = state.documentNotifications
      .filter((documentNotification: IDocumentNotification) =>
        state.selectedDocumentIds.some((id: string) => id === documentNotification.document.id)
      )
      .map((documentNotification: IDocumentNotification) => documentNotification.id);
    try {
      await service.deleteNotifications(documentNotificationIds);
      dispatch(actionDeleteNotificationSuccess, documentNotificationIds);
    } catch (error) {
      dispatch(actionDeleteNotificationFailed, error);
    }
  },
  async [actionDeleteNotificationSuccess]({ commit }, documentNotificationIds: string[]) {
    commit(mutationDeleteDocumentNotifications, documentNotificationIds);
  },
  async [actionDeleteNotificationFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-remove-document-notifications'), {
      root: true,
    });
  },
  async [actionDeleteSingleNotification]({ dispatch }, notificationId: string) {
    const documentNotificationId: string[] = Array.of(notificationId);
    try {
      await service.deleteNotifications(documentNotificationId);
      dispatch(actionDeleteNotificationSuccess, documentNotificationId);
    } catch (error) {
      dispatch(actionDeleteNotificationFailed, error);
    }
  },
  async [actionLoadDocuments]({ state, commit, dispatch }) {
    try {
      commit(mutationLoadingDocuments, true);
      const documents = await service.loadDocuments(
        state.folderId,
        state.filter,
        state.filterActive
      );
      commit(mutationLoadingDocuments, false);
      dispatch(actionLoadDocumentsSuccess, documents);
    } catch (error) {
      commit(mutationLoadingDocuments, false);
    }
  },
  async [actionLoadDocumentsSuccess]({ commit, dispatch }, newDocuments: IDocument[]) {
    commit(mutationSetDocuments, newDocuments);
    dispatch(actionDeselectAllDocuments);
  },
  async [actionDownloadDocuments]({ dispatch, state }) {
    dispatch(
      'alert/showInfoAlert',
      translate('Download for {count} documents has started', {
        count: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
    try {
      const downloadDocuments: IDownloadDocument[] = await service.loadDownloadUrls(
        state.selectedDocumentIds
      );
      dispatch(actionDownloadToClient, downloadDocuments);
    } catch (error) {
      dispatch(actionDownloadFailed, { error });
    }
  },
  async [actionDownloadToClient]({ dispatch }, downloadDocuments: IDownloadDocument[]) {
    for (const { documentName, documentLink } of downloadDocuments) {
      try {
        const { data, headers } = await axios({
          url: documentLink,
          method: 'GET',
          responseType: 'blob',
        });
        const contentType: string = headers['content-type'];
        const fileExtension: string = contentType.split('/')[1];

        const url: string = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${documentName}.${fileExtension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        dispatch(actionDownloadFailed, { e, downloadDocuments });
      }
    }
  },
  async [actionDownloadFailed]({ dispatch, state }) {
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to download {documentsCount} documents', {
        documentsCount: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
  },
  async [actionLoadFolders]({ commit, state }) {
    const folders: IFolder[] = await FolderService.loadFolders(state.folderId);
    commit(mutationSetFolders, folders);
  },
  async [actionSetSelectedFolder]({ dispatch }, folder: IFolder) {
    try {
      const folders: IFolder[] = await FolderService.loadFolders(folder.id);
      dispatch(actionSetSelectedFolderSuccess, { folder, folders });
    } catch (error) {
      dispatch(actionSetSelectedFolderFailed, {
        error,
        folderName: folder.name,
      });
    }
  },
  async [actionSetSelectedFolderSuccess](
    { commit, dispatch },
    { folder, folders }: { folder: IFolder; folders: IFolder[] }
  ) {
    dispatch(actionDeselectAllDocuments);
    commit(mutationSetSelectedFolder, { folder, folders });
  },
  async [actionSetSelectedFolderFailed](
    { dispatch },
    { folderName }: { error: any; folderName: string }
  ) {
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to select folder "{name}"', { name: folderName }),
      {
        root: true,
      }
    );
  },
  async [actionLoadBreadcrumb]({ commit, state }) {
    const rootFolder: IFolder = { id: null, name: 'Start' };
    const breadcrumb: IFolder[] = await FolderService.loadBreadcrumbs(state.folderId);
    commit(mutationSetBreadcrumb, [rootFolder, ...breadcrumb]);
  },
  async [actionDeleteDocuments]({ dispatch, state }) {
    try {
      await service.deleteDocuments(state.selectedDocumentIds);
      dispatch(actionDeleteDocumentsSuccess);
    } catch (error) {
      dispatch(actionDeleteDocumentsFailed, { error });
    }
  },
  async [actionDeleteDocumentsSuccess]({ dispatch, commit, state }) {
    commit(mutationDeleteDocuments);
    dispatch(
      'alert/showSuccessAlert',
      translate('{documentsCount} documents successfully deleted', {
        documentsCount: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
    dispatch(actionLoadDocuments);
  },
  async [actionDeleteDocumentsFailed]({ dispatch, state }) {
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to delete {documentsCount} documents', {
        documentsCount: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
  },
  async [actionUpdateDocuments]({ dispatch, state }, payload: IEditDocumentsPayload) {
    try {
      const { user, ...formattedPayload } = payload;
      const variables: IEditDocumentsPayload = user
        ? { ...formattedPayload, userId: user.id }
        : formattedPayload;
      await service.editDocuments({
        payload: variables,
        documentIds: state.selectedDocumentIds,
      });
      dispatch(actionUpdateDocumentsSuccess, payload);
    } catch (error) {
      dispatch(actionUpdateDocumentsFailed, error);
    }
  },
  async [actionUpdateDocumentsSuccess](
    { commit, dispatch, state },
    payload: IEditDocumentsPayload
  ) {
    commit(mutationEditDocuments, payload);
    dispatch(
      'alert/showSuccessAlert',
      translate('{documentsCount} documents successfully edited', {
        documentsCount: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
  },
  async [actionUpdateDocumentsFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('Failed to save the edited documents'), {
      root: true,
    });
  },
  async [actionApplyFilter]({ commit, dispatch, state }) {
    try {
      dispatch(actionLoadFolders);
      dispatch(actionLoadBreadcrumb);
      dispatch(actionLoadFilterRanges);
      if (state.filterInitialized) {
        dispatch(actionLoadDocuments);
        dispatch(actionLoadKeyValues);
      }
    } catch (e) {
      commit(mutationError, e.message);
    }
  },
  async [actionLoadKeyValues]({ dispatch, state }) {
    try {
      const keyValues: IContractManagementKeyValues = await service.loadKeyValues(
        state.folderId,
        state.filter,
        state.filterActive
      );
      dispatch(actionLoadKeyValuesSuccess, keyValues);
    } catch (error) {
      console.error(error);
    }
  },
  async [actionLoadKeyValuesSuccess]({ commit }, keyValues: IContractManagementKeyValues) {
    commit(mutationSetKeyValues, keyValues);
  },
  async [actionCreateFolder]({ commit, state }, name) {
    try {
      const folder = await service.createFolder(name, state.folderId);
      commit(mutationSetFolders, [...state.folders, folder]);
    } catch (e) {
      commit(mutationError, e.message);
    }
  },
  async [actionUpdateFilter]({ commit, state, dispatch }, setting: IFilterSetting) {
    const filter = {
      ...state.filter,
      ...setting,
    };
    dispatch(actionSetFilterActiveStatus, filter);
    commit(mutationSetFilter, filter);
  },
  async [actionUpdateFilterDocumentType]({ commit, state }, documentType: EnumDocumentType) {
    const filter: IContractManagementFilter = {
      ...state.filter,
      documentType,
    };
    commit(mutationSetFilter, filter);
  },
  async [actionSetDefaultFilter](
    { commit, state, dispatch },
    filterRanges: IContractManagementFilterRanges
  ) {
    const defaultFilter: IContractManagementFilter = getDefaultFilter(filterRanges, state.filter);
    commit(mutationSetDefaultFilter, defaultFilter);
    dispatch(actionLoadDocuments);
    dispatch(actionLoadKeyValues);
  },
  async [actionSetFilterActiveStatus]({ commit, state }, filter: IContractManagementFilter) {
    const isFilterActive: boolean = filterIsActive(state.defaultFilter, filter);
    commit(mutationSetFilterActiveState, isFilterActive);
  },
  async [actionResetFilter]({ commit, state, dispatch }) {
    const defaultFilter: IContractManagementFilter = getDefaultFilter(
      state.filterRanges,
      state.filter
    );
    commit(mutationSetDefaultFilter, defaultFilter);
    dispatch(actionSetFilterActiveStatus, state.filter);
  },
  async [actionOpenFolder]({ commit, dispatch }, folderId: string) {
    commit(mutationOpenFolder, folderId);
    dispatch(actionApplyFilter);
  },
  async [actionLoadFilterRanges]({ commit, state, dispatch }) {
    const filterRanges: IContractManagementFilterRanges = await service.loadFilterRanges();
    commit(mutationSetFilterRanges, filterRanges);
    if (!state.filterInitialized) {
      dispatch(actionSetDefaultFilter, filterRanges);
    }
  },
  async [actionLoadAllTags]({ dispatch }) {
    try {
      const allTags: ITag[] = await service.loadAllTags();
      dispatch(actionLoadAllTagsSuccess, allTags);
    } catch (error) {
      console.error(error);
    }
  },
  async [actionLoadAllTagsSuccess]({ commit }, tags: ITag[]) {
    commit(mutationSetAllTags, tags);
  },
  async [actionUpdateDocumentsFolder]({ dispatch, state }, folderId: string) {
    try {
      await service.updateDocumentsFolder(state.selectedDocumentIds, folderId);
      dispatch(actionUpdateDocumentsFolderSuccess);
    } catch (error) {
      dispatch(actionUpdateDocumentsFolderFailed, {
        error,
        folderId: folderId,
      });
    }
  },
  async [actionUpdateDocumentsFolderSuccess]({ dispatch }) {
    dispatch(actionDeselectAllDocuments);
    dispatch(actionLoadFolders);
    dispatch(actionLoadDocuments);
    dispatch(actionLoadKeyValues);
  },
  async [actionUpdateDocumentsFolderFailed](
    { state, dispatch },
    { folderId }: { error: any; folderId: string }
  ) {
    const folderName: string = [state.folderTree[0], ...state.folderTree.folders].find(
      (folder: IFolder) => folder.id === folderId
    ).name;
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to move {documentsCount} documents to folder "{name}"', {
        documentsCount: state.selectedDocumentIds.length,
        name: folderName,
      }),
      {
        root: true,
      }
    );
  },
  async [actionUploadDocument]({ dispatch }, data: IUploadDocument) {
    dispatch(
      'alert/showInfoAlert',
      translate('Started upload for document {name}', {
        name: data.name,
      }),
      {
        root: true,
      }
    );
    try {
      const uploadDocument: IDocument = await service.uploadDocument(data);
      dispatch(actionUploadDocumentSuccess, uploadDocument);
    } catch (error) {
      dispatch(actionUploadDocumentFailed, { error, data });
    }
  },
  async [actionUploadDocumentSuccess]({ dispatch }, document: IDocument) {
    dispatch(
      'alert/showSuccessAlert',
      translate('Document {name} successfully uploaded', {
        name: document.name,
      }),
      {
        root: true,
      }
    );
    dispatch(actionLoadDocuments);
  },
  async [actionUploadDocumentFailed](
    { dispatch },
    { data }: { error: any; data: IUploadDocument }
  ) {
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to upload document "{name}"', { name: data.name }),
      {
        root: true,
      }
    );
  },
  async [actionUpdateFolder]({ dispatch }, folder: IFolder) {
    try {
      const updatedFolder = await service.updateFolderName(folder.id, folder.name);
      dispatch(actionUpdateFolderSuccess, updatedFolder);
    } catch (error) {
      console.error(error);
    }
  },
  async [actionUpdateFolderSuccess]({ commit }, folder: IFolder) {
    commit(mutationUpdateFolder, folder);
  },
  async [actionDeleteFolder]({ dispatch, state }) {
    try {
      await service.deleteFolder(state.selectedFolder.id);
      dispatch(actionDeleteFolderSuccess, state.selectedFolder);
    } catch (error) {
      dispatch(actionDeleteFolderFailed, {
        error,
        folderName: state.selectedFolder.name,
      });
    }
  },
  async [actionDeleteFolderSuccess]({ commit, dispatch }, folder: IFolder) {
    commit(mutationDeleteFolder, folder.id);
    dispatch(
      'alert/showSuccessAlert',
      translate('Folder "{name}" successfully deleted', { name: folder.name }),
      {
        root: true,
      }
    );
  },
  async [actionDeleteFolderFailed](
    { dispatch },
    { folderName }: { error: any; folderName: string }
  ) {
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to delete folder "{name}"', { name: folderName }),
      {
        root: true,
      }
    );
  },
  async [actionCreateTag]({ dispatch }, title: string) {
    try {
      const createTag = await service.createTag(title);
      dispatch(actionCreateTagSuccess, createTag);
    } catch (error) {
      dispatch(actionCreateTagFailed, { error, title });
    }
  },
  async [actionCreateTagSuccess]({ commit }, newTag: ITag) {
    commit(mutationAddTag, newTag);
  },
  async [actionCreateTagFailed]({ dispatch }, { title }: { error: string; title: string }) {
    dispatch('alert/showDangerAlert', translate('Failed to create tag "{name}"', { name: title }), {
      root: true,
    });
  },
  async [actionAddDocumentTags]({ dispatch, state, getters }, selectedTags: ITag[]) {
    const { selectedDocuments } = getters;
    const tagIds = selectedTags.map((tag: ITag) => tag.id);

    const taggedDocuments = await selectedDocuments.map((selectedDocument: IDocument) => {
      selectedDocument.tags =
        selectedDocument.tags !== undefined
          ? [
              ...selectedDocument.tags,
              ...selectedTags.filter(
                (newTag: ITag) =>
                  !selectedDocument.tags.some(
                    (selectedDocumentTag: ITag) => selectedDocumentTag.id === newTag.id
                  )
              ),
            ]
          : selectedTags;
      return selectedDocument;
    });
    try {
      await service.addDocumentTags(state.selectedDocumentIds, tagIds);
      dispatch(actionAddDocumentTagsSuccess, taggedDocuments);
    } catch (error) {
      dispatch(actionAddDocumentTagsFailed, { error, tagIds });
    }
  },
  async [actionAddDocumentTagsSuccess]({ commit, dispatch }, taggedDocuments) {
    commit(mutationAddDocumentTags, taggedDocuments);
    dispatch(
      'alert/showSuccessAlert',
      translate('{documentsCount} documents successfully tagged', {
        documentsCount: taggedDocuments.length,
      }),
      {
        root: true,
      }
    );
  },
  async [actionAddDocumentTagsFailed]({ dispatch }, { tagIds }: { error: any; tagIds: string[] }) {
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to add {tagsCount} tags', { tagsCount: tagIds.length }),
      {
        root: true,
      }
    );
  },
  async [actionRemoveAllTags]({ dispatch, state, getters }) {
    const { selectedDocuments } = getters;
    const tagIds = selectedDocuments.flatMap((document: IDocument) =>
      document.tags.map((tag: ITag) => tag.id)
    );
    const taggedDocuments = await selectedDocuments.map((selectedDocument: IDocument) => {
      selectedDocument.tags = [];
      return selectedDocument;
    });
    try {
      await service.deleteDocumentTags(state.selectedDocumentIds, tagIds);
      dispatch(actionRemoveAllTagsSuccess, taggedDocuments);
    } catch (error) {
      console.error(error);
    }
  },
  async [actionRemoveAllTagsSuccess]({ commit, dispatch }, taggedDocuments) {
    commit(mutationAddDocumentTags, taggedDocuments);
    dispatch(
      'alert/showSuccessAlert',
      translate('Tags successfully removed from {documentsCount} documents', {
        documentsCount: taggedDocuments.length,
      }),
      {
        root: true,
      }
    );
  },
};
